import React from "react";

export default function ChevyBolt({ activeSubTab, handleSubTabClick, carLabel }) {
    const subTabs = [
        { id: "charging", label: "Charging" },
        { id: "driving", label: "Driving" },
        { id: "features", label: "Features" },
        { id: "faq", label: "FAQ" },
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <h1 className="text-3xl font-extrabold text-gray-900 pl-3 border-l-4 border-blue-600">
                {carLabel}
            </h1>


            {/* Sub-tabs (desktop only) */}
            <div className="hidden md:flex gap-2 flex-wrap">
                {subTabs.map((sub) => (
                    <button
                        key={sub.id}
                        onClick={() => handleSubTabClick("bolt", sub.id)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition ${activeSubTab === sub.id ||
                            (!activeSubTab && sub.id === "charging")
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                    >
                        {sub.label}
                    </button>
                ))}
            </div>

            {/* Charging */}
            {(!activeSubTab || activeSubTab === "charging") && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🔌 Home Charging (Level 1)</h2>
                        <p className="text-gray-700">
                            Plug into any standard 120V outlet using the included J-1772 charger. This is the cheapest option
                            (usually under $10 for a full charge), but also the slowest — expect up to 3 days for 0–100% — around 4
                            miles of range per hour of charging.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">⚡ Public Level 2 Charging</h2>
                        <p className="text-gray-700">
                            Level 2 chargers (240V) are common at workplaces, hotels, and shopping centers. Expect ~10 hours
                            for a full charge. Use apps like PlugShare or <a href="https://chargefinder.com/us" target="_blank"
                                rel="noopener noreferrer" className="text-blue-600 underline">ChargeFinder</a> to locate stations.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🚀 DC Fast Charging (CCS)</h2>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>Uses the CCS port (not compatible with Tesla’s (NACS) Supercharging network - verify on <a
                                href="https://chargefinder.com/us" target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 underline">ChargeFinder</a>).</li>
                            <li>Max charging rate: 45 kW → 0–80% in ~1 hour.</li>
                            <li>Even if a charger advertises 150 kW, the Bolt can use it to charge but it will only draw 45 kW.</li>
                            <li>Cost example: At $0.12/kWh, a full 100% (60 kWh/~250 miles of range) charge costs ~$7.20.</li>
                        </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">⚠️ Charging Troubleshooting</h2>
                        <p className="text-gray-700">
                            Some DC fast chargers have heavy cords that may not seat properly. If charging won’t start:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-1">
                            <li>Unplug and firmly reseat the connector.</li>
                            <li>Try another stall if available.</li>
                            <li>Check if activation is required via app or RFID card.</li>
                            <li>If all else fails, try holding the charging cord against the charge port as the sheer weight of the
                                CCS connector can sometimes prevent a good connection. This is a known issue with this generation
                                Bolt. See link below for more information.</li>
                        </ul>
                        <p className="mt-2 text-sm text-gray-500">
                            Helpful resource:{" "}
                            <a href="https://www.chevybolt.org/threads/dcfast-charging-issue.55897/" target="_blank"
                                rel="noopener noreferrer" className="text-blue-600 underline">
                                ChevyBolt.org charging issue thread
                            </a>
                        </p>
                    </div>
                </div>
            )}

            {/* Driving */}
            {activeSubTab === "driving" && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🚗 Starting the Car</h2>
                        <p className="text-gray-700">
                            The Bolt starts silently. Press the brake and push the power button. Shift into Drive (D) to go.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🦶 One-Pedal Driving</h2>
                        <p className="text-gray-700">
                            Shift into “L” mode to enable full regenerative braking. The car slows significantly when you lift
                            off the accelerator, so you’ll rarely need the brake pedal. Great for city driving and maximizing range.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🎮 Regen Paddle</h2>
                        <p className="text-gray-700">
                            On the back left of the steering wheel is a paddle that temporarily engages regen braking.
                            Use it to slow down without switching into “L” mode.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🛣️ Highway Driving</h2>
                        <p className="text-gray-700">
                            At highway speeds, range decreases faster. Plan charging stops in advance using ChargeFinder or
                            PlugShare.
                            Cruise control helps maintain efficiency.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">📏 Range Management</h2>
                        <p className="text-gray-700">
                            Expect ~238 miles on a full charge under ideal conditions. Cold weather, high speeds, or heavy loads
                            will reduce range. Pre‑condition the cabin while plugged in to save battery.
                        </p>
                    </div>
                </div>
            )}

            {/* Features */}
            {activeSubTab === "features" && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">📱 Infotainment</h2>
                        <p className="text-gray-700">
                            Apple CarPlay and Android Auto are supported via USB. The touchscreen also provides energy usage
                            stats and navigation.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">❄️ Comfort</h2>
                        <p className="text-gray-700">
                            Heated seats and steering wheel are controlled from the center console. Climate control is electric,
                            so pre‑conditioning while plugged in helps preserve range.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">🛡️ Safety & Parking</h2>
                        <p className="text-gray-700">
                            Rearview camera and parking sensors make tight spots easier. The car is very quiet — always check
                            surroundings.
                        </p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold mb-2">📦 Storage & Practicality</h2>
                        <p className="text-gray-700">
                            The Bolt has a surprisingly roomy hatchback design. Rear seats fold flat for extra cargo space.
                        </p>
                    </div>
                </div>
            )}

            {/* FAQ */}
            {activeSubTab === "faq" && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-2xl font-bold mb-4">❓ Chevy Bolt FAQ</h2>
                        <div className="divide-y divide-gray-200">
                            <div className="py-3">
                                <p className="font-semibold">Can I use a Tesla Supercharger?</p>
                                <p className="text-gray-700">No. The Bolt only supports CCS plugs.</p>
                            </div>
                            <div className="py-3">
                                <p className="font-semibold">How long does charging take?</p>
                                <p className="text-gray-700">
                                    Level 1: ~3 days. Level 2: ~10 hours. DC Fast: ~1 hour (0–80%).
                                </p>
                            </div>
                            <div className="py-3">
                                <p className="font-semibold">What if the charger won’t start?</p>
                                <p className="text-gray-700">
                                    Reseat the connector, try another stall, or check if activation via app/card is required. If all
                                    of those fail, try holding the charging cord against the charge port as the sheer weight of the
                                    CCS connector can sometimes prevent a good connection. (See thread: <a
                                        href="https://www.reddit.com/r/chevrolet/comments/xyz123/chevy_bolt_ev_charging_issues/"
                                        className="text-blue-600">here</a>)
                                </p>
                            </div>
                            <div className="py-3">
                                <p className="font-semibold">Can I plug the car into CCS chargers, even if it only charges at 45kW?
                                </p>
                                <p className="text-gray-700">
                                    Yes. The car supports CCS charging and will safely connect to chargers of any speed (50kW,
                                    150kW, even 250kW or higher). However, the vehicle itself is limited to a maximum charging rate
                                    of 45kW.
                                    <ul>
                                        <li>- Plugging into a faster charger won’t harm the car, but it won’t charge any quicker than
                                            45kW.</li>
                                        <li>- Keep in mind: higher‑speed chargers often cost more per kWh, so a 50kW charger is usually
                                            the most cost‑effective choice.</li>
                                    </ul>
                                </p>
                            </div>
                            <div className="py-3">
                                <p className="font-semibold">Do I need to return it fully charged?</p>
                                <p className="text-gray-700">
                                    Please return with the same battery level as pickup unless otherwise arranged.
                                </p>
                            </div>
                            <div className="py-3">
                                <p className="font-semibold">What’s the real‑world range?</p>
                                <p className="text-gray-700">
                                    Expect ~238 miles on a full charge under ideal conditions. Cold weather, high speeds, or heavy
                                    loads will reduce range.
                                </p>
                            </div>
                            <div className="py-3">
                                <p className="font-semibold">Is the car compatible with J‑1772 plugs</p>
                                <p className="text-gray-700">
                                    Yes. All CCS‑equipped vehicles are also compatible with J‑1772. The car comes with a home
                                    charger that uses a J‑1772 plug, so you can easily charge at home or at public Level 2 stations.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}